import React, { useState } from 'react';
import { Box, Typography, Collapse, List, ListItem, ListItemText, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DT_SPECIAL_WIDGETS, GENERAL_WIDGET_GROUPS } from './registry/dtWidgetTypes';

function SectionHeader({ label, expanded, onToggle, isDark }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 12px',
        cursor: 'pointer',
        backgroundColor: isDark ? '#252525' : '#f0f0f0',
        '&:hover': { backgroundColor: isDark ? '#2a2a2a' : '#e8e8e8' },
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#aaa' : '#555', textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {label}
      </Typography>
      {expanded ? <ExpandLessIcon sx={{ fontSize: 14, color: isDark ? '#aaa' : '#666' }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: isDark ? '#aaa' : '#666' }} />}
    </Box>
  );
}

function WidgetItem({ item, onAdd, isDark }) {
  return (
    <ListItem
      button
      onClick={() => onAdd(item.type)}
      sx={{
        padding: '4px 12px',
        '&:hover': { backgroundColor: isDark ? '#1e3a5f' : '#e3f2fd' },
        cursor: 'pointer',
      }}
    >
      <Typography sx={{ mr: 1, fontSize: 14 }}>{item.icon}</Typography>
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{ sx: { fontSize: 12, color: isDark ? '#ddd' : '#333', fontWeight: 500 } }}
      />
    </ListItem>
  );
}

function DTWidgetLibrary({ onAddWidget, theme }) {
  const isDark = theme === 'dark';
  const [dtOpen, setDtOpen] = useState(true);
  const [generalOpen, setGeneralOpen] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  return (
    <Box
      sx={{
        width: 220,
        height: '100%',
        overflow: 'auto',
        backgroundColor: isDark ? '#1a1a1a' : '#fafafa',
        borderRight: `1px solid ${isDark ? '#333' : '#ddd'}`,
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', padding: '8px 12px', fontWeight: 700, color: isDark ? '#888' : '#555', textTransform: 'uppercase', letterSpacing: 1 }}>
        Widget Library
      </Typography>
      <Divider />

      {/* Digital Twin Special */}
      <SectionHeader label="DT Special" expanded={dtOpen} onToggle={() => setDtOpen((p) => !p)} isDark={isDark} />
      <Collapse in={dtOpen}>
        <List disablePadding dense>
          {DT_SPECIAL_WIDGETS.map((w) => (
            <WidgetItem key={w.type} item={w} onAdd={onAddWidget} isDark={isDark} />
          ))}
        </List>
      </Collapse>

      <Divider />

      {/* General Platform Widgets */}
      <SectionHeader label="General" expanded={generalOpen} onToggle={() => setGeneralOpen((p) => !p)} isDark={isDark} />
      <Collapse in={generalOpen}>
        {Object.entries(GENERAL_WIDGET_GROUPS).map(([groupName, items]) => (
          <Box key={groupName}>
            <SectionHeader
              label={groupName}
              expanded={!!expandedGroups[groupName]}
              onToggle={() => toggleGroup(groupName)}
              isDark={isDark}
            />
            <Collapse in={!!expandedGroups[groupName]}>
              <List disablePadding dense>
                {items.map((w) => (
                  <WidgetItem key={w.type} item={w} onAdd={onAddWidget} isDark={isDark} />
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </Collapse>
    </Box>
  );
}

export default React.memo(DTWidgetLibrary);
